import { filesService } from '../services/FilesService';
import type { FileMetadata } from '@cnapse/shared/schemas.js';
import { resolveThumbnailObjectKey } from '@cnapse/shared/utils/thumbnail-object-key.js';
import type { FilePreviewDescriptor } from '../types/files';

/**
 * Batch-resolve presigned download URLs for storage object keys.
 * Browser loads these directly from S3 (no backend byte streaming).
 */
export async function resolvePresignedUrlsByObjectKey(
  objectKeys: string[],
): Promise<Record<string, string>> {
  const unique = [...new Set(objectKeys.map((k) => k.trim()).filter((k) => k.length > 0))];
  if (unique.length === 0) {
    return {};
  }

  const response = await filesService.getPresignedObjectUrls(unique, undefined, 60_000);
  const out: Record<string, string> = {};
  for (const key of unique) {
    const url = response[key]?.url;
    if (url) {
      out[key] = url;
    }
  }
  return out;
}

async function resolvePresignedUrlForDescriptor(
  descriptor: FilePreviewDescriptor,
): Promise<string | null> {
  const objectKey = descriptor.objectKey?.trim();
  if (!objectKey) {
    return null;
  }

  const presigned = await resolvePresignedUrlsByObjectKey([objectKey]);
  return presigned[objectKey] ?? null;
}

/**
 * Resolve a single preview URL as a presigned S3 URL (requires objectKey).
 */
export async function resolveFilePreviewUrl(
  descriptor: FilePreviewDescriptor,
): Promise<string | null> {
  try {
    return await resolvePresignedUrlForDescriptor(descriptor);
  } catch (error) {
    console.warn('Presigned preview URL failed', descriptor.objectKey, error);
    return null;
  }
}

/**
 * Resolve preview URLs for many descriptors via batch presigned S3 URLs.
 */
export async function resolveFilePreviewUrls(
  descriptors: FilePreviewDescriptor[],
): Promise<(string | null)[]> {
  if (descriptors.length === 0) {
    return [];
  }

  const objectKeys = descriptors
    .map((descriptor) => descriptor.objectKey?.trim())
    .filter((key): key is string => Boolean(key));

  let presignedMap: Record<string, string> = {};
  try {
    presignedMap = await resolvePresignedUrlsByObjectKey(objectKeys);
  } catch (error) {
    console.warn('Presigned URL batch resolve failed', error);
  }

  return descriptors.map((descriptor) => {
    const objectKey = descriptor.objectKey?.trim();
    if (!objectKey) {
      return null;
    }
    return presignedMap[objectKey] ?? null;
  });
}

export interface ImagePreviewUrls {
  thumbnailUrl: string | null;
  fullUrl: string | null;
}

/**
 * Resolve thumbnail + full presigned URLs for grid/lightbox image previews.
 * Grid should prefer thumbnailUrl; lightbox should use fullUrl.
 */
export async function resolveImagePreviewUrlPairs(
  images: Pick<FileMetadata, 'objectKey' | 'thumbnailObjectKey'>[],
): Promise<ImagePreviewUrls[]> {
  if (images.length === 0) {
    return [];
  }

  const thumbKeys = images.map((img) =>
    resolveThumbnailObjectKey(img.objectKey, img.thumbnailObjectKey),
  );
  const fullKeys = images.map((img) => img.objectKey);
  const allKeys = [...new Set([...thumbKeys, ...fullKeys])];

  let presignedMap: Record<string, string> = {};
  try {
    presignedMap = await resolvePresignedUrlsByObjectKey(allKeys);
  } catch (error) {
    console.warn('Presigned URL batch resolve failed', error);
  }

  return images.map((img, index) => {
    const thumbKey = thumbKeys[index];
    const fullUrl = presignedMap[img.objectKey] ?? null;
    const thumbnailUrl = presignedMap[thumbKey] ?? fullUrl;
    return { thumbnailUrl, fullUrl };
  });
}

export async function resolveImageFullPreviewUrl(
  objectKey: string,
): Promise<string | null> {
  const trimmed = objectKey.trim();
  if (!trimmed) {
    return null;
  }
  const presigned = await resolvePresignedUrlsByObjectKey([trimmed]);
  return presigned[trimmed] ?? null;
}

/** Trigger a browser download from a presigned S3 URL (direct S3, no backend proxy). */
export function triggerPresignedDownload(presignedUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = presignedUrl;
  link.download = filename;
  link.rel = 'noopener';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function filenameFromObjectKey(objectKey: string): string {
  const segment = objectKey.split('/').pop();
  return segment?.trim() ? segment : 'download';
}

/**
 * Download a tracked file via presigned S3 URL (requires objectKey).
 */
export async function downloadTrackedFile(
  descriptor: FilePreviewDescriptor,
  suggestedFilename?: string,
): Promise<void> {
  const objectKey = descriptor.objectKey?.trim();
  if (!objectKey) {
    throw new Error('objectKey is required for download');
  }

  const presigned = await resolvePresignedUrlsByObjectKey([objectKey]);
  const url = presigned[objectKey];
  if (!url) {
    throw new Error('Could not resolve download URL for object key');
  }

  triggerPresignedDownload(url, suggestedFilename ?? filenameFromObjectKey(objectKey));
}
